import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getProduct, getCategory, getGroup, products } from "../data/products";
import { ui, useLang } from "../i18n/i18n";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const { t } = useLang();

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-32" data-testid="product-not-found">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">Error · 404</div>
        <h1 className="mt-4 text-4xl md:text-5xl font-semibold text-[#0B1F3A]">
          {t(ui.common.productNotFound)}
        </h1>
        <p className="mt-4 text-[#4B5563]">{t(ui.common.productNotFoundDesc)}</p>
        <Link to="/products" className="mt-8 inline-flex items-center gap-2 text-[#0B1F3A] link-underline">
          <ArrowLeft size={16} /> {t(ui.common.backToCatalog)}
        </Link>
      </div>
    );
  }

  const isTool = product.group === "tools-ppe";
  const productCategories = isTool
    ? []
    : (product.categories || []).map((s) => getCategory(s)).filter(Boolean);

  const related = products
    .filter((p) => {
      if (p.slug === product.slug) return false;
      if (isTool) return p.group === "tools-ppe";
      if (p.group === "tools-ppe") return false;
      return (p.categories || []).some((c) => (product.categories || []).includes(c));
    })
    .slice(0, 3);

  const specKeyLabel = (k) => (ui.specKeys[k] ? t(ui.specKeys[k]) : k);
  const headerMeta = isTool
    ? t(ui.productLabel["tools-ppe"])
    : `${t(ui.detail.productLabel)} · ${product.spec?.["CAS No."] || "—"}`;
  const toolGroup = getGroup("tools-ppe");

  return (
    <div data-testid="product-detail-page">
      {/* Breadcrumb */}
      <section className="border-b border-[#E5E7EB]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex items-center gap-3 text-sm text-[#6B7280] overflow-x-auto">
          <Link to="/" className="hover:text-[#0B1F3A]" data-testid="breadcrumb-home">
            {t(ui.detail.home)}
          </Link>
          <span>/</span>
          <Link to="/products" className="hover:text-[#0B1F3A]" data-testid="breadcrumb-products">
            {t(ui.detail.productsCrumb)}
          </Link>
          <span>/</span>
          <span className="text-[#0B1F3A]">{t(product.name)}</span>
        </div>
      </section>

      {/* Header */}
      <section className="border-b border-[#E5E7EB]" data-testid="product-header">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 md:py-20">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-8">
              <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                {headerMeta}
              </div>
              <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
              <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0B1F3A] leading-[1.02]">
                {t(product.name)}
              </h1>
              {product.synonym && (
                <div className="mt-3 text-lg text-[#6B7280] italic">{t(product.synonym)}</div>
              )}
              <p className="mt-8 text-[#4B5563] text-[17px] md:text-[19px] max-w-2xl leading-relaxed">
                {t(product.summary)}
              </p>
            </div>

            <div className="col-span-12 md:col-span-4">
              <div className="border border-[#E5E7EB] p-6">
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {t(ui.common.listedIn)}
                </div>
                <div className="mt-4 flex flex-col gap-3">
                  {isTool && toolGroup && (
                    <Link
                      to="/products?group=tools-ppe"
                      data-testid="product-group-tools-ppe"
                      className="flex items-center justify-between gap-3 pt-3 border-t border-[#E5E7EB] text-[#0B1F3A] hover:bg-[#F7F8FA] -mx-6 px-6 py-3 transition-colors"
                    >
                      <div>
                        <div className="mono text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                          {toolGroup.code}
                        </div>
                        <div className="text-sm font-medium mt-1">{t(toolGroup.name)}</div>
                      </div>
                      <ArrowUpRight size={16} />
                    </Link>
                  )}
                  {productCategories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/products?category=${c.slug}`}
                      data-testid={`product-category-${c.slug}`}
                      className="flex items-center justify-between gap-3 pt-3 border-t border-[#E5E7EB] text-[#0B1F3A] hover:bg-[#F7F8FA] -mx-6 px-6 py-3 transition-colors"
                    >
                      <div>
                        <div className="mono text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                          {c.code}
                        </div>
                        <div className="text-sm font-medium mt-1">{t(c.name)}</div>
                      </div>
                      <ArrowUpRight size={16} />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="border-b border-[#E5E7EB]" data-testid="product-description">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-4">
              <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                {t(ui.detail.descEyebrow)}
              </div>
              <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
              <h2 className="mt-6 text-2xl md:text-3xl font-semibold text-[#0B1F3A] tracking-tight">
                {t(ui.detail.overview)}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="text-[#4B5563] text-[17px] leading-relaxed">{t(product.description)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Specification */}
      {product.spec && (
        <section className="border-b border-[#E5E7EB] bg-[#F7F8FA]" data-testid="product-spec">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
            <div className="grid grid-cols-12 gap-10">
              <div className="col-span-12 md:col-span-4">
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {t(ui.detail.specEyebrow)}
                </div>
                <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
                <h2 className="mt-6 text-2xl md:text-3xl font-semibold text-[#0B1F3A] tracking-tight">
                  {t(ui.detail.techData)}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-7 md:col-start-6">
                <table className="w-full spec-table">
                  <tbody>
                    {Object.entries(product.spec).map(([k, v]) => (
                      <tr key={k} data-testid={`spec-${k.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                        <th>{specKeyLabel(k)}</th>
                        <td>{t(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Packaging */}
      {product.packaging && product.packaging.length > 0 && (
        <section className="border-b border-[#E5E7EB]" data-testid="product-packaging">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
            <div className="grid grid-cols-12 gap-10">
              <div className="col-span-12 md:col-span-4">
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {t(ui.detail.pkgEyebrow)}
                </div>
                <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
                <h2 className="mt-6 text-2xl md:text-3xl font-semibold text-[#0B1F3A] tracking-tight">
                  {t(ui.detail.pkgTitle)}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-7 md:col-start-6">
                <ul className="border-t border-[#E5E7EB]">
                  {product.packaging.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-6 py-4 border-b border-[#E5E7EB]"
                      data-testid={`packaging-${i}`}
                    >
                      <span className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF] pt-1">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[#0B1F3A] text-[15px]">{t(item)}</span>
                    </li>
                  ))}
                </ul>
                {(product.shelfLife || product.storage) && (
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                    {product.shelfLife && (
                      <div>
                        <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                          {t(ui.detail.shelfLife)}
                        </div>
                        <div className="mt-2 text-[#0B1F3A]">{t(product.shelfLife)}</div>
                      </div>
                    )}
                    {product.storage && (
                      <div>
                        <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                          {t(ui.detail.storage)}
                        </div>
                        <div className="mt-2 text-[#0B1F3A] text-[15px] leading-relaxed">
                          {t(product.storage)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="border-b border-[#E5E7EB]" data-testid="related-products">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  {t(ui.detail.relatedEyebrow)}
                </div>
                <h2 className="mt-4 text-2xl md:text-3xl font-semibold text-[#0B1F3A] tracking-tight">
                  {t(ui.common.relatedProducts)}
                </h2>
              </div>
              <Link to="/products" className="hidden md:inline-flex link-underline text-[#0B1F3A] text-sm">
                {t(ui.common.allProducts)}
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/products/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="bg-white p-6 md:p-8 hover:bg-[#F7F8FA] transition-colors"
                >
                  <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                    {p.spec?.["HS Code"] || "—"}
                  </div>
                  <div className="mt-3 text-xl font-semibold text-[#0B1F3A] tracking-tight">
                    {t(p.name)}
                  </div>
                  <p className="mt-3 text-sm text-[#4B5563] line-clamp-2">{t(p.summary)}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-[#0B1F3A] mono text-[10px] uppercase tracking-[0.2em]">
                    {t(ui.common.view)} <ArrowUpRight size={12} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
